import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Master data › Site visits — QR check-in points (client / channel partner),
// the visit-alert team, and the check-in log with client ↔ partner pairs.
const BOX = { background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '16px', border: '1px solid #e5e7eb' };
const H = { fontSize: '15px', fontWeight: 700, margin: '0 0 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' };
const IN = { width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const TH = { textAlign: 'left', fontSize: '12px', color: '#6b7280', fontWeight: 600, padding: '6px 8px', borderBottom: '1px solid #e5e7eb' };
const TD = { padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', verticalAlign: 'middle' };
const BTN = { padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '13px' };
const DEL = { ...BTN, borderColor: '#fca5a5', color: '#dc2626' };
const EMPTY = { name: '', phone: '', role: '', notify_client: true, notify_partner: true, active: true };
const STATUS = { pending: 'Waiting 5 min for a pair', merged: 'Sent with its pair', sent: 'Alert sent', failed: 'Alert failed', no_recipients: 'No one to alert', sending: 'Sending…' };

const QrImage = ({ apiBase, point }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    axios.get(`${apiBase}/site-checkin/points/${point.id}/qr.png`, { responseType: 'blob' })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [apiBase, point.id, point.point_key]);
  if (!src) return <div style={{ width: 180, height: 180, background: '#f3f4f6', borderRadius: 8 }} />;
  return (
    <div style={{ textAlign: 'center' }}>
      <img src={src} alt={`${point.label} QR`} style={{ width: 180, height: 180, opacity: point.active ? 1 : 0.3 }} />
      <div><a href={src} download={`${point.kind}-checkin-qr.png`} style={{ fontSize: '13px' }}>Download QR</a></div>
    </div>
  );
};

const SiteVisits = ({ apiBase }) => {
  const [points, setPoints] = useState([]);
  const [team, setTeam] = useState([]);
  const [log, setLog] = useState([]);
  const [edit, setEdit] = useState(null); // {id?, ...fields}
  const [newPoint, setNewPoint] = useState({ kind: 'client', label: '' });
  const [err, setErr] = useState('');
  const [appr, setAppr] = useState({ approvers: [], can_edit: false });
  const [na, setNa] = useState({ name: '', phone: '' });
  const loadAppr = useCallback(() => { axios.get(`${apiBase}/booking-approval/approvers/list`).then((r) => setAppr(r.data)).catch(() => {}); }, [apiBase]);
  useEffect(() => { loadAppr(); }, [loadAppr]);
  const addAppr = async () => { try { await axios.post(`${apiBase}/booking-approval/approvers`, na); setNa({ name: '', phone: '' }); loadAppr(); } catch (e) { setErr(e.response?.data?.error || 'Could not add'); } };
  const toggleAppr = async (a) => { try { await axios.put(`${apiBase}/booking-approval/approvers/${a.id}`, { active: !a.active }); loadAppr(); } catch (e) { setErr(e.response?.data?.error || 'Could not update'); } };

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${apiBase}/site-checkin/points`),
      axios.get(`${apiBase}/site-checkin/recipients`),
      axios.get(`${apiBase}/site-checkin/checkins`),
    ]).then(([p, t, l]) => { setPoints(p.data); setTeam(t.data); setLog(l.data); })
      .catch((e) => setErr(e.response?.status === 403 ? 'Only admins can manage site visits.' : 'Could not load site visits'));
  }, [apiBase]);
  useEffect(() => { load(); }, [load]);

  const fail = (e, d) => setErr(e.response?.data?.error || d);
  const saveMember = async () => {
    setErr('');
    try {
      if (edit.id) await axios.put(`${apiBase}/site-checkin/recipients/${edit.id}`, edit);
      else await axios.post(`${apiBase}/site-checkin/recipients`, edit);
      setEdit(null); load();
    } catch (e) { fail(e, 'Could not save'); }
  };
  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from visit alerts?`)) return;
    try { await axios.delete(`${apiBase}/site-checkin/recipients/${m.id}`); load(); } catch (e) { fail(e, 'Could not remove'); }
  };
  const togglePoint = async (p) => {
    try { await axios.put(`${apiBase}/site-checkin/points/${p.id}`, { active: !p.active }); load(); } catch (e) { fail(e, 'Could not update'); }
  };
  const addPoint = async () => {
    if (!newPoint.label.trim()) { setErr('Name the check-in point'); return; }
    try { await axios.post(`${apiBase}/site-checkin/points`, newPoint); setNewPoint({ kind: 'client', label: '' }); load(); } catch (e) { fail(e, 'Could not add'); }
  };

  const row = (m) => (edit && edit.id === m.id ? (
    <tr key={m.id}>
      <td style={TD}><input style={IN} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></td>
      <td style={TD}><input style={IN} value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} inputMode="tel" /></td>
      <td style={TD}><input style={IN} value={edit.role || ''} onChange={(e) => setEdit({ ...edit, role: e.target.value })} /></td>
      <td style={TD}><input type="checkbox" checked={edit.notify_client} onChange={(e) => setEdit({ ...edit, notify_client: e.target.checked })} /></td>
      <td style={TD}><input type="checkbox" checked={edit.notify_partner} onChange={(e) => setEdit({ ...edit, notify_partner: e.target.checked })} /></td>
      <td style={TD}><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /></td>
      <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
        <button style={BTN} onClick={saveMember}>Save</button> <button style={BTN} onClick={() => setEdit(null)}>Cancel</button>
      </td>
    </tr>
  ) : (
    <tr key={m.id} style={{ opacity: m.active ? 1 : 0.5 }}>
      <td style={{ ...TD, fontWeight: 600 }}>{m.name}</td>
      <td style={TD}>{m.phone}</td>
      <td style={TD}>{m.role || '—'}</td>
      <td style={TD}>{m.notify_client ? '✓' : '—'}</td>
      <td style={TD}>{m.notify_partner ? '✓' : '—'}</td>
      <td style={TD}>{m.active ? 'On' : 'Off'}</td>
      <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
        <button style={BTN} onClick={() => setEdit({ ...m })}>Edit</button> <button style={DEL} onClick={() => removeMember(m)}>Remove</button>
      </td>
    </tr>
  ));

  return (
    <div>
      {err && <div style={{ ...BOX, background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' }}>{err}</div>}

      <div style={BOX}>
        <div style={H}><span>QR check-in points</span></div>
        <p style={{ fontSize: '13px', color: '#4b5563', marginTop: 0 }}>
          Print these at the site. Scanning opens WhatsApp (The Real Connect, +91 98200 76730) with a check-in code typed in; one tap
          records the walk-in here and the Kohinoor bot takes over. A client and a channel partner who scan within 5 minutes of each
          other are reported together.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          {points.map((p) => (
            <div key={p.id} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', width: '230px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: p.kind === 'partner' ? '#7c3aed' : '#059669', textTransform: 'uppercase' }}>
                {p.kind === 'partner' ? 'Channel partner' : 'Client'}
              </div>
              <div style={{ fontWeight: 600, margin: '4px 0 10px', fontSize: '14px' }}>{p.label}</div>
              <QrImage apiBase={apiBase} point={p} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '13px' }}>
                <span>{p.active ? '🟢 Active' : '⚪ Off'}</span>
                <button style={BTN} onClick={() => togglePoint(p)}>{p.active ? 'Turn off' : 'Turn on'}</button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
          <select style={{ ...IN, width: '170px' }} value={newPoint.kind} onChange={(e) => setNewPoint({ ...newPoint, kind: e.target.value })}>
            <option value="client">Client QR</option><option value="partner">Channel partner QR</option>
          </select>
          <input style={{ ...IN, width: '280px' }} placeholder="e.g. Sales office — Clients" value={newPoint.label} onChange={(e) => setNewPoint({ ...newPoint, label: e.target.value })} />
          <button style={BTN} onClick={addPoint}>+ Add check-in point</button>
        </div>
      </div>

      <div style={BOX}>
        <div style={H}>
          <span>Visit alert team</span>
          <button style={BTN} onClick={() => setEdit({ ...EMPTY, id: 0 })}>+ Add person</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={TH}>Name</th><th style={TH}>WhatsApp</th><th style={TH}>Role</th><th style={TH}>Client visits</th><th style={TH}>Partner visits</th><th style={TH}>Alerts</th><th style={TH} /></tr></thead>
          <tbody>
            {team.map(row)}
            {edit && edit.id === 0 && row({ id: 0 })}
          </tbody>
        </table>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: 0 }}>Alerts go out on WhatsApp from The Real Connect about 5 minutes after a check-in (so a pair can be combined).</p>
      </div>

      <div style={BOX}>
        <div style={H}><span>Booking approvers (partners)</span></div>
        <p style={{ fontSize: '13px', color: '#4b5563', marginTop: 0 }}>Any one of these partners approves a booking — on WhatsApp or in the CRM (login phone must match). Their name and the time are recorded and can never be changed. {appr.can_edit ? '' : 'Only the superadmin can change this list.'}</p>
        {appr.approvers.map((a) => (
          <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '14px', opacity: a.active ? 1 : 0.5 }}>
            <span><b>{a.name}</b> · {a.phone}</span>
            <span>{a.active ? 'Active' : 'Off'} {appr.can_edit && <button style={{ ...BTN, marginLeft: '8px' }} onClick={() => toggleAppr(a)}>{a.active ? 'Turn off' : 'Turn on'}</button>}</span>
          </div>
        ))}
        {appr.can_edit && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            <input style={{ ...IN, width: '200px' }} placeholder="Partner name" value={na.name} onChange={(e) => setNa({ ...na, name: e.target.value })} />
            <input style={{ ...IN, width: '160px' }} placeholder="Mobile" inputMode="tel" value={na.phone} onChange={(e) => setNa({ ...na, phone: e.target.value })} />
            <button style={BTN} onClick={addAppr}>+ Add partner</button>
          </div>
        )}
      </div>

      <div style={BOX}>
        <div style={H}><span>Recent check-ins</span><button style={BTN} onClick={load}>Refresh</button></div>
        {log.length === 0 ? <div style={{ fontSize: '13px', color: '#9ca3af' }}>No check-ins yet.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={TH}>When</th><th style={TH}>Who</th><th style={TH}>Phone</th><th style={TH}>Point</th><th style={TH}>Came with</th><th style={TH}>Alert</th></tr></thead>
              <tbody>
                {log.map((c) => {
                  const pair = c.paired_checkin_id ? log.find((x) => x.id === c.paired_checkin_id) : null;
                  const who = c.kind === 'client' ? `Client · ${c.client_name || c.wa_name || '—'}` : `Partner · ${c.partner_name || c.wa_name || '—'}${c.partner_firm ? `, ${c.partner_firm}` : ''}`;
                  const withWho = pair ? (pair.kind === 'client' ? (pair.client_name || pair.wa_name) : `${pair.partner_name || pair.wa_name}${pair.partner_firm ? `, ${pair.partner_firm}` : ''}`) : '—';
                  return (
                    <tr key={c.id}>
                      <td style={{ ...TD, whiteSpace: 'nowrap' }}>{new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                      <td style={TD}>{who}</td>
                      <td style={TD}>{c.phone}</td>
                      <td style={{ ...TD, fontSize: '12px' }}>{c.label}</td>
                      <td style={TD}>{withWho}</td>
                      <td style={{ ...TD, fontSize: '12px', color: c.alert_status === 'failed' ? '#dc2626' : '#374151' }} title={c.alert_error || ''}>{STATUS[c.alert_status] || c.alert_status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteVisits;
